// Version Checker - Auto-detects new deployments on user interaction
const APP_VERSION_KEY = 'app_version';
const CHECK_COOLDOWN = 30 * 1000; // Only check once per 30 seconds to avoid spam

export class VersionChecker {
    private currentVersion: string;
    private onNewVersionCallback: (() => void) | null = null;
    private lastCheckTime: number = 0;
    private isChecking: boolean = false;

    constructor() {
        // Get version from meta tag or generate from build time
        const metaVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
        this.currentVersion = metaVersion || this.generateVersion();

        // Store initial version
        this.storeVersion(this.currentVersion);
    }

    private generateVersion(): string {
        // Use build timestamp as version
        return document.querySelector('meta[name="build-time"]')?.getAttribute('content') || Date.now().toString();
    }

    private storeVersion(version: string): void {
        try {
            localStorage.setItem(APP_VERSION_KEY, version);
        } catch (e) {
            console.warn('Could not store version in localStorage', e);
        }
    }

    async checkForNewVersion(): Promise<boolean> {
        // Cooldown check - don't spam the server
        const now = Date.now();
        if (now - this.lastCheckTime < CHECK_COOLDOWN) {
            return false;
        }

        // Prevent concurrent checks
        if (this.isChecking) {
            return false;
        }

        try {
            this.isChecking = true;
            this.lastCheckTime = now;

            // Fetch the HTML page with cache-busting
            const response = await fetch(`/?_=${Date.now()}`, {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) return false;

            const html = await response.text();

            // Extract version from meta tag in fetched HTML
            const versionMatch = html.match(/<meta name="app-version" content="([^"]+)"/);
            const newVersion = versionMatch ? versionMatch[1] : null;

            if (newVersion && newVersion !== this.currentVersion) {
                console.log(`New version detected: ${newVersion} (current: ${this.currentVersion})`);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Version check failed:', error);
            return false;
        } finally {
            this.isChecking = false;
        }
    }

    startChecking(onNewVersion: () => void): void {
        this.onNewVersionCallback = onNewVersion;

        // Check on user interactions
        const handleInteraction = async () => {
            const hasNewVersion = await this.checkForNewVersion();
            if (hasNewVersion && this.onNewVersionCallback) {
                this.onNewVersionCallback();
                // Remove listeners after showing notification once
                this.stopChecking();
            }
        };

        // Listen for clicks and navigation
        document.addEventListener('click', handleInteraction);
        window.addEventListener('popstate', handleInteraction); // Browser back/forward

        // Store listeners for cleanup
        (this as any)._clickListener = handleInteraction;
        (this as any)._popstateListener = handleInteraction;
    }

    stopChecking(): void {
        // Remove event listeners
        if ((this as any)._clickListener) {
            document.removeEventListener('click', (this as any)._clickListener);
            delete (this as any)._clickListener;
        }
        if ((this as any)._popstateListener) {
            window.removeEventListener('popstate', (this as any)._popstateListener);
            delete (this as any)._popstateListener;
        }
    }

    forceRefresh(): void {
        // Clear all caches and reload
        if ('caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
            });
        }

        // Clear localStorage version
        try {
            localStorage.removeItem(APP_VERSION_KEY);
        } catch (e) {
            // Ignore
        }

        // Hard reload
        window.location.reload();
    }
}

export const versionChecker = new VersionChecker();
